<script setup lang="ts">
// eslint-disable-next-line
// @ts-ignore
import { Modal } from 'bootstrap/dist/js/bootstrap.bundle.min.js'
import { onMounted, onUnmounted, ref, useSlots } from 'vue'
import { useMouseInElement } from '@vueuse/core'
import { useDevice } from '@/composables/useDevice'
import { useI18n } from 'vue-i18n'

type Size = 'sm' | 'md' | 'lg' | 'xl' | 'full'

interface Props {
  title?: string
  id: string
  size?: Size
  bg?: string
  static?: boolean
  enableClose?: boolean
  bodyClasses?: string
  headerClasses?: string
  borderClasses?: string
  newStyle?: boolean
  textColor?: string
  titleTop?: boolean
  showCloseText?: boolean
  mobileDesign?: boolean
  desactivateBackdrop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  bg: '',
  static: false,
  enableClose: true,
  bodyClasses: '',
  headerClasses: '',
  borderClasses: 'rounded-1',
  newStyle: false,
  textColor: 'text-dark',
  titleTop: false,
  showCloseText: true,
  mobileDesign: false,
})

const modalRef = ref<HTMLElement>()
const modal = ref()
const modalContentRef = ref()
const { t } = useI18n()
const { isOutside } = useMouseInElement(modalContentRef)
const currentTime = ref<Date>()
const MAX_TIME_TO_WAIT_CLOSE_MODAL_IN_MILLIS = 500
const slots = useSlots()
const { isMobile } = useDevice()

onMounted(() => {
  modal.value = new Modal(modalRef.value)

  const modalElement = document.getElementById(props.id)
  if (modalElement) {
    modalElement.onclick = () => {
      currentTime.value = new Date()
    }

    modalElement.addEventListener('hidden.bs.modal', () => {
      if (
        isOutside.value &&
        currentTime.value &&
        new Date().getTime() - currentTime.value.getTime() < MAX_TIME_TO_WAIT_CLOSE_MODAL_IN_MILLIS
      ) {
        emits('backdropClicked')
      }
    })
  }
})

onUnmounted(() => {
  hide()
})

function show() {
  modal.value.show()
}

function hide() {
  modal.value.hide()
}

function getBackdropValue() {
  return props.desactivateBackdrop ? false : props.static ? 'static' : true
}

const emits = defineEmits<{
  (event: 'close'): void
  (event: 'backdropClicked'): void
}>()

defineExpose({
  show,
  hide,
})
</script>

<template>
  <div
    :id="props.id"
    ref="modalRef"
    class="modal fade"
    :class="{ 'align-content-end': mobileDesign }"
    :data-bs-backdrop="getBackdropValue()"
    :data-bs-keyboard="!props.static"
    tabindex="-1"
    aria-hidden="true"
  >
    <div
      class="modal-dialog-centered modal-dialog"
      :class="[
        props.size && !isMobile ? `modal-${props.size}` : '',
        { 'm-0': props.titleTop },
        { 'align-items-end mb-0 w-100 mx-0': props.mobileDesign && isMobile },
        { 'mx-auto': isMobile },
      ]"
      :style="{ maxWidth: props.mobileDesign && isMobile ? 'none' : '' }"
    >
      <div
        v-if="props.titleTop"
        class="position-absolute top-0 w-100 bg-primary d-flex justify-content-center py-2 letter-spacing-05 fw-light"
      >
        <button
          v-if="enableClose"
          type="button"
          class="border-0 bg-transparent px-0 start-0 position-absolute px-2"
          data-bs-dismiss="modal"
          @click.stop="emits('close')"
        >
          <i-icon-park-outline-close class="d-flex align-self-center fs-6" :class="props.textColor" /></button
        >{{ props.title }}
      </div>
      <div
        ref="modalContentRef"
        class="modal-content overflow-auto"
        :class="[props.bg, props.borderClasses, { 'hstack align-items-start mt-5': props.mobileDesign && isMobile }]"
      >
        <div
          v-if="props.title && !props.newStyle && !props.titleTop"
          class="modal-header"
          :class="props.headerClasses ? props.headerClasses : 'border-0 mx-1 mt-2 pb-0'"
        >
          <h3 class="modal-title" :class="props.textColor">{{ props.title }}</h3>

          <button
            v-if="enableClose"
            type="button"
            class="d-flex align-items-center border-0 bg-transparent px-0"
            data-bs-dismiss="modal"
            @click.stop="emits('close')"
          >
            <span v-if="props.showCloseText" class="me-2" :class="props.textColor">{{ t('Close') }}</span>
            <i-icon-park-outline-close class="d-flex align-self-center" :class="props.textColor" />
          </button>
        </div>

        <div
          class="modal-body"
          :class="[props.bodyClasses ? props.bodyClasses : 'mx-1 mb-3', { 'pt-0': isMobile && props.mobileDesign }]"
        >
          <div
            v-if="props.mobileDesign && isMobile"
            data-bs-dismiss="modal"
            class="modal-header border-0 cursor-pointer pt-4"
            @click.stop="emits('close')"
          >
            <div type="button" class="w-100 d-flex justify-content-center">
              <div class="bg-dark rounded opacity-25" :style="{ width: '2.5rem', height: '4px' }"></div>
            </div>
          </div>
          <div v-else-if="props.newStyle" class="d-flex align-items-center justify-content-between">
            <h4 class="mb-0 me-2">{{ props.title }}</h4>

            <button
              v-if="enableClose"
              type="button"
              class="d-flex border-0 bg-transparent px-0"
              data-bs-dismiss="modal"
              @click.stop="emits('close')"
            >
              <i-bi-x class="fs-3 text-black-50 align-self-center" />
            </button>
          </div>
          <slot></slot>
        </div>

        <div v-if="slots.footer" class="modal-footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/variables';

// We use the Ids of the modals so as not to break other modals
#passport-template-modal,
#create-passport-selector-modal,
#smart-label-modal {
  left: $sidebar-width * 0.5 !important;
}

.modal-full {
  max-width: 100% !important;
  margin-left: 3rem;
  margin-right: 3rem;
}

.modal-md {
  width: 450px !important;
}

.rounded-15 {
  //Equivalent to the middle of rounded-1 and rounded-2
  border-radius: 0.5rem !important;
}
</style>
